/* eslint-disable no-param-reassign */
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import getClientUsers, {
	GetClientUsersOptions,
	GetClientUsersResponse,
} from 'api/users/getClientUsers'
import { ClientUser } from 'types'

/**
 * Types for the slice of client users
 */
export interface UsersState {
	records: ClientUser[]
	totalCount: number
	totalPages: number
	currentPage: number
	loading: boolean
	error: string | null
}

/**
 * Initial state of the slice client
 */
const initialState: UsersState = {
	records: [],
	totalCount: 0,
	loading: false,
	currentPage: 1,
	totalPages: 0,
	error: null,
}

/**
 * Action for getting clients
 *
 * Tries to get clients from the API and returns the result in the payload
 *
 * @param {GetClientUsersOptions} options - Options for the API call
 * @returns {Promise<GetClientUsersResponse>} - Response object returned from the server
 * @throws {Error} - Throws an error if the API call fails
 */

export const getAllClientUsersAction = createAsyncThunk<
	GetClientUsersResponse,
	GetClientUsersOptions
>('client/getAllClientUsersAction', async (options, { rejectWithValue }) => {
	try {
		const response = await getClientUsers(options)
		return response
	} catch (error: any) {
		const message =
			error?.response?.data?.message ??
			(typeof error?.response?.data === 'string' ? error.response.data : null) ??
			error?.message
		return rejectWithValue(message || 'Could not load users.')
	}
})

export const usersSlice = createSlice({
	name: 'users',
	initialState,
	reducers: {
		clearUsers() {
			return initialState
		},
	},
	extraReducers: builder => {
		builder.addCase(getAllClientUsersAction.pending, state => {
			state.loading = true
			state.error = null
		})
		builder.addCase(getAllClientUsersAction.fulfilled, (state, { payload }) => {
			state.records = payload.records
			state.totalCount = payload.totalCount
			state.totalPages = payload.totalPages
			state.currentPage = payload.currentPage
			state.loading = false
			state.error = null
		})
		builder.addCase(getAllClientUsersAction.rejected, (state, { payload }) => {
			state.loading = false
			state.error =
				(typeof payload === 'string' && payload) ||
				'Could not load users. The client’s trial may have ended or the request failed.'
		})
	},
})

export const { clearUsers } = usersSlice.actions

export default usersSlice.reducer
